import * as React from 'react'
import { Box } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { Dayjs } from 'dayjs'
import { CustomStyles, getStyles } from '@utils/styles'
import defaultStyles, { StylesClassNames } from './styles'

// 🛠 helper to clean invalid sx props before passing to MUI
const cleanSx = (sx: any | null | undefined): any => {
  if (!sx || typeof sx !== 'object') return {}
  const invalidKeys = ['offset', 'accentColor']
  const cleaned: Record<string, unknown> = {
    ...(sx as Record<string, unknown>),
  }
  for (const key of invalidKeys) {
    if (key in cleaned) delete cleaned[key]
  }
  return cleaned as any
}

export type CommonDatePickerProps = {
  label?: string
  initialValue?: Dayjs
  minDate?: Dayjs
  maxDate?: Dayjs
  onDateChange: (date: Dayjs) => void
  shouldDisableDate?: (date: Dayjs) => boolean
}

type Props = CommonDatePickerProps & {
  customStyles?: CustomStyles<StylesClassNames>
}

export function CommonDatePicker({
  label,
  initialValue,
  onDateChange,
  customStyles,
  minDate,
  maxDate,
  shouldDisableDate,
}: Props) {
  const styles = getStyles(defaultStyles, customStyles)
  const [selectedDate, setSelectedDate] = React.useState<Dayjs | null>(
    initialValue || null
  )

  const handleDateChange = (value: Dayjs | null) => {
    if (value !== null) {
      onDateChange(value)
      setSelectedDate(value)
    }
  }

  // ✅ Ensure sx is always a valid object and free of invalid keys
  const datePickerSx = React.useMemo(() => {
    return cleanSx(styles('datePicker')?.sx)
  }, [styles])

  return (
    <Box {...styles('wrapper')}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label={label}
          value={selectedDate}
          views={['year', 'month', 'day']}
          sx={datePickerSx}
          minDate={minDate}
          onChange={handleDateChange}
          maxDate={maxDate}
          shouldDisableDate={shouldDisableDate}
        />
      </LocalizationProvider>
    </Box>
  )
}
